import React, { useCallback, useEffect, useState } from 'react';
import { storeApi, StoreItem } from '../../services/storeApi';
import { fallbackUploadsSrc, getImageUrl } from '../../utils/imageUrl';

const StoreCatalog: React.FC = () => {
  const [items, setItems] = useState<StoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await storeApi.listPublicItems();
      setItems(data.items || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Không tải được sản phẩm');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const openLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#C4A35A] border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-6 text-center">
        <p className="text-sm text-red-700">{error}</p>
        <button
          type="button"
          onClick={() => void loadItems()}
          className="mt-3 text-sm font-medium text-[#8C5226] underline"
        >
          Thử lại
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[#E8D8BF] bg-[#FFFCF5] px-4 py-10 text-center">
        <i className="fas fa-store mb-3 text-2xl text-[#C4A35A]" />
        <p className="text-sm font-medium text-black">Chưa có sản phẩm</p>
        <p className="mt-1 text-xs text-black/50">Admin sẽ thêm sản phẩm vào cửa hàng</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {items.map((item) => {
        const imageSrc = getImageUrl(item.image_url);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => openLink(item.link_url)}
            className="group overflow-hidden rounded-xl border border-black/10 bg-white text-left shadow-sm transition hover:border-[#C4A35A]/40 hover:shadow-md"
          >
            <div className="aspect-square overflow-hidden bg-gray-100">
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={item.title}
                  className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                  loading="lazy"
                  onError={(event) => {
                    const next = fallbackUploadsSrc(event.currentTarget.src, item.image_url);
                    if (next) event.currentTarget.src = next;
                  }}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-black/30">
                  <i className="fas fa-image text-2xl" />
                </div>
              )}
            </div>
            <div className="p-2.5">
              <p className="line-clamp-2 text-sm font-medium text-black">{item.title}</p>
              {item.description ? (
                <p className="mt-0.5 line-clamp-2 text-xs text-black/50">{item.description}</p>
              ) : null}
              <p className="mt-1.5 text-[11px] font-medium text-[#8C5226]">
                Đặt hàng <i className="fas fa-external-link-alt ml-0.5 text-[10px]" />
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default StoreCatalog;
